import {useEffect, useState } from "react";
import * as Yup from "yup";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {confirmPasswordReset } from "firebase/auth";
import { auth } from "../../firebase";
import { ToastContainer, toast } from "react-toastify";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import axios from "axios";
import { baseUrl, GetToken } from "../../App";

const UserVerification: React.FC = ()=> {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode");
  const emailVerification = searchParams.get("mode") === "resetPassword" && oobCode ? true : false
  const [newPassword, setNewPassword] = useState("");
  const [inputedCode, setInputedCode] = useState<string>()
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@_+/.,:*&%$#!~`\\|])[A-Za-z\d@_+/.,:*&%$#!~`\\|-]{8,}$/;

  const schema = Yup.object({
    newPassword: Yup.string()
      .matches(
        passwordRegex,
        "Password must be at least 8 characters long, contain a number, a capital letter, and a special character."
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], "Passwords must match")
      .required("Confirm Password is required"),
  });

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setIsResendEnabled(true);
    }
  }, [timeLeft]);

  const formatTime = (seconds:number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const resendCode = async () => {
    let userEmail = location?.state?.userEmail || localStorage.getItem("userEmail");

    if(!userEmail || userEmail.length < 1){
      toast("No user email was passed");
      return;
    }
    setTimeLeft(180);
    setIsResendEnabled(false);
    let idToken = await GetToken();
    let APIRequest = {
      Email: userEmail
    }
    const response = await axios.post(`${baseUrl}/sendVerificationCode`, APIRequest,
      {
        headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }).catch((error) =>
    {
      console.log("From the User Verification page")
      console.log(error);
      toast("Validation error occured");
    });
    console.log(response);
  }

  const validateCode = async () =>{
    let userEmail = location?.state?.userEmail || localStorage.getItem("userEmail");

    if(!userEmail || userEmail.length < 1){
      toast("No user email was passed");
      return;
    }

    let code = Number(inputedCode);
    if(!inputedCode || typeof(code) !== "number"){
      toast("Invalid input! Please input a 6-digit code");
      return;
    }
    let idToken = await GetToken();
    let APIRequest = {
      Email: userEmail,
      Code: code
    }
    const response = await axios.post(`${baseUrl}/verifyCode`, APIRequest,
      {
        headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }).catch(()=>
    {
      toast("Validation error occured");
    });
    if(response?.data?.Status === "Success"){
      setIsDisabled(true);
      toast("Verified! You can sign in now!")
      localStorage.removeItem("userEmail");
      setTimeout(()=>{
        navigate("/signin");
      },2000);
    }else{
      toast(response?.data?.Message);
    }
  }

  const validatePasswords = async () => {
    try {
      await schema.validate({ newPassword, confirmPassword });
      return true;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        toast(error.errors[0] || "Validation error occurred.");
      } else {
        toast("An unexpected error occurred during validation.");
      }
      return false;
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const canProceed = await validatePasswords();
    if (canProceed) {
      setIsDisabled(true);
      if (!oobCode) {
        toast("Invalid or missing reset code.");
        return;
      }
      confirmPasswordReset(auth, oobCode, newPassword)
        .then(() => {
          toast("Password reset successful, please sign in!");
          setTimeout(() => {
            navigate("/signin");
          }, 3000);
        })
        .catch((error) => {
          setIsDisabled(false);
          console.error("Error resetting password:", error);
          toast("Failed to reset the password. Please try again.");
      });
    }
  };


  return (
    <>
    <ToastContainer autoClose={2000} />
    <div className="felx justify-center items-center">
      <div className="bg-[#b9eddc] h-[100vh] overflow-hidden flex flex-col items-center justify-center md:py-5">
        <Link to="/">
          <img
            src="../../images/doyenifyAcademy.png"
            style={{ cursor: "pointer", marginBottom: "20px" }}
            alt="acadlogo"
          />
        </Link>

        <div className="bg-[#fff] w-4/5 lg:w-[30%] xl:w-[30%] rounded-xl py-3 shadow-md px-5">
          {emailVerification === true ? (
            <p className="inter-600 font-bold text-xl w-auto text-center text-[#2b2b2b] my-2">
              Reset Password
            </p>
          ) : (
            <p className="inter-600 font-bold text-xl w-auto text-center text-[#2b2b2b] my-2">
              Verify Email
            </p>
          )}
          <div className="flex items-center w-full mt-2">
            <hr className="bg-gray-100 w-full" />
          </div>

          {emailVerification === true && (
            <form onSubmit={handleResetPassword} className="flex flex-col justify-center">
              <label className="text-base font-normal text-[#3f3f3f] block mt-3 mb-3">
                New Password <br />
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full mt-1 border-2 border-gray-200 rounded-xl py-2 px-2 focus:outline-none placeholder:text-sm placeholder:text-[#757575] placeholder:font-normal"
                    placeholder="******"
                    required
                  />

                  <button
                    type="button"
                    className="absolute top-1/2 transform -translate-y-1/2 right-5 text-gray-500"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <AiOutlineEyeInvisible />
                    ) : (
                      <AiOutlineEye />
                    )}
                  </button>
                </div>
              </label>

              <label className="text-base font-normal text-[#3f3f3f] block mb-3">
                Confirm Password <br />
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full mt-1 border-2 border-gray-200 rounded-xl py-2 px-2 focus:outline-none placeholder:text-sm placeholder:text-[#757575] placeholder:font-normal"
                    placeholder="******"
                    required
                  />
                  <button
                    type="button"
                    className="absolute top-1/2 transform -translate-y-1/2 right-5 text-gray-500"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <AiOutlineEyeInvisible />
                    ) : (
                      <AiOutlineEye />
                    )}
                  </button>
                </div>
              </label>
              <button
                className={`w-3/4 mx-auto border border-[#1f5845] text-[#ffff] ${isDisabled ? `bg-[#63b197]`: `bg-[#1f5845]`} font-semibold rounded-full py-3 p mt-4`}
                type="submit"
                disabled={isDisabled}
              >
                Reset Password
              </button>
            </form>
          )}
          {!emailVerification && (
            <div className="flex flex-col justify-center">
              <p className="text-center mb-4">
                A 6-digit verification code has been sent to your email. Please
                input it below.
              </p>
              <input
                type="text"
                onChange={(e)=>setInputedCode(e.target.value)}
                className="border rounded-md p-2"
                placeholder="Enter OTP"
                required
              ></input>
              <div className="pt-1 text-[12px] flex justify-between">
                <p>Didn't receive code?</p>
                <p>
                 <span 
                 className={`${
                  isResendEnabled ? "text-[#0F4B16] font-semibold" : "text-black font-light cursor-not-allowed"
                }`}
                onClick={()=>{if (isResendEnabled) resendCode();}}> Resend </span>
                 <span className="text-black font-light">{formatTime(timeLeft)}</span>{" "}
                </p>
              </div>
              <button
                disabled={isDisabled}
                className={`w-3/4 mx-auto border border-[#1f5845] text-[#ffff] ${isDisabled ? `bg-[#2ba83a]` : `bg-[#0F4B16]`}  font-semibold rounded-full py-3 p mt-4`}
                onClick={() => {
                  validateCode();
                }}
              >
                {isDisabled ? "Verified!": "Verify Code"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default UserVerification;
